import dayjs from "dayjs";
import Vue from "vue";
import { formatNumberAsPercentage } from "~/mixins/mixins";
import { decodeUrlForPlace } from "~/operations/shared";
import {
  formatTableRowData as formatTableRowDataUtil,
  generateTableHeaderData as generateTableHeaderDataUtil,
  returnStateTableHeaderData as returnStateTableHeaderDataUtil,
  TableItem,
  TableRowOptions,
} from "~/utils/tableUtils";

interface PriceHistoryPoint {
  changedAtUnix?: number;
  x?: number;
  priceData?: number;
  y?: number;
}

interface StatePrice {
  name: string;
  currentPrice: {
    value: number;
    modifiedAt: number;
  };
  lastPrice: {
    value: number;
    modifiedAt: number;
  };
  zipcode?: string;
  link: string;
}

interface CityPrice {
  name: string;
  seoName: string;
  zipcode: string;
  price: {
    actual: number;
    previous: number;
  };
}

interface CityPriceData {
  priceData: CityPrice[];
}

// Need to to use Vue.extend for TypeScript compatibility
// These any types are hard to avoid in this context
export default Vue.extend({
  methods: {
    /**
     * Generates table header data with formatted dates
     */
    generateTableHeaderData(
      mainColumnTitle: string,
      timestamps: number[] = [],
    ): string[] {
      return generateTableHeaderDataUtil(mainColumnTitle, timestamps);
    },

    /**
     * Returns formatted table header data for states
     */
    returnStateTableHeaderData(state: TableItem[]): string[] {
      return returnStateTableHeaderDataUtil(state);
    },

    /**
     * Formats table row data with sorting and formatting options
     */
    formatTableRowData(items: TableItem[], options: TableRowOptions) {
      return formatTableRowDataUtil(items, options);
    },

    /**
     * Formats table data for states/regions with appropriate links
     * and filters out states with no prices
     */
    formatTopStatesTableData(
      state: StatePrice[],
      productType = "heizoelpreise",
    ) {
      const filteredStates = state.filter(
        (item) => item.currentPrice && item.currentPrice.value > 0,
      );

      return this.formatTableRowData(filteredStates, {
        hrefTemplate: (item: any) => {
          const specialStates = ["bremen", "berlin", "hamburg"];
          const lowerCaseName = item.name.toLowerCase();

          return specialStates.includes(lowerCaseName)
            ? `/${productType}/${productType}-${lowerCaseName}-${item.zipcode}`
            : item.link.includes(productType)
              ? item.link
              : item.link.replace("/heizoelpreise/", `/${productType}/`);
        },
        differenceCalculation: (item: any) =>
          item.currentPrice.value - item.lastPrice.value,
      });
    },

    /**
     * Formats table data for big cities
     * and filters out cities with no prices
     */
    formatBigCitiesTableData(
      state: CityPriceData,
      productType: "heizoelpreise" | "pelletspreise" = "heizoelpreise",
    ) {
      const filteredCities = state.priceData.filter(
        (item) => item.price && item.price.actual > 0,
      );

      const filteredState = {
        ...state,
        priceData: filteredCities,
      };

      return this.formatTableRowData(filteredState.priceData, {
        sortKey: "price.actual",
        hrefTemplate: (item: any) =>
          decodeUrlForPlace(
            `${productType}-${item.seoName}-${item.zipcode}`,
            productType,
          ),
        differenceCalculation: (item: any) =>
          item.price.actual - item.price.previous,
        includeNumber: false,
      });
    },

    /**
     * Retrieves price from a specified number of days ago
     */
    getPriceFromDaysAgo(priceData: PriceHistoryPoint[], daysAgo: number) {
      const pastTimestamp = dayjs().subtract(daysAgo, "day").unix();
      const pastData = priceData
        .filter((data) => (data.changedAtUnix || data.x) <= pastTimestamp)
        .sort((a, b) => {
          const aTimestamp = a.changedAtUnix || a.x;
          const bTimestamp = b.changedAtUnix || b.x;
          return bTimestamp - aTimestamp;
        })[0];

      return pastData ? pastData.priceData || pastData.y : null;
    },

    /**
     * Calculates percentage differences between two prices
     */
    calculatePercentageDifference(
      currentPrice: number,
      previousPrice: number,
    ): number | null {
      if (previousPrice === null || currentPrice === null) {
        return null;
      }

      return ((currentPrice - previousPrice) / previousPrice) * 100;
    },

    /**
     * Formats price comparison text
     */
    formatPriceComparisonText(
      currentPrice: number,
      previousPrice: number,
    ): string {
      if (previousPrice === null || currentPrice === null) {
        return "";
      }

      const percentageDifference = this.calculatePercentageDifference(
        currentPrice,
        previousPrice,
      );
      const formattedPercentage = formatNumberAsPercentage(
        Number(percentageDifference),
      );

      if (currentPrice < previousPrice) {
        return `um ${formattedPercentage} gesunken`;
      } else if (currentPrice > previousPrice) {
        return `um ${formattedPercentage} gestiegen`;
      } else {
        return 'unverändert'
      }
    },
  },
});
